import { Form } from './form';
import { Config } from '../config';
import { StripeValidator } from '../validators/stripe-validator';
import { StripeView } from '../view/stripe-view';
import { FormTypes } from './form-types';
import { Logger } from '../logger';
import { Order } from '../config/order';
import { PaymentType } from '../providers/payment-type';

declare const Stripe: any;

export class StripeForm extends Form {

    private stripeAPI: any;
    private stripeElement: any;
    logger: Logger = {} as Logger;
    view: StripeView = new StripeView();
    type = FormTypes.Stripe;
    isNoTotal = false;

    constructor(step: number | null = null) {
        super();
        this.step = step;
    }

    async init(config: Config, callback: any) {
        await super.init(config, callback);
        return this.loadScript('https://js.stripe.com/v3/')
            .then(() => this.validators.push(new StripeValidator()));
    }

    render() {
        if (!this.isNoPayment) {
            this.view.renderStripeFormFragment(this.config.settings.stripeSplitElements);
            this.stripeAPI = Stripe(this.config.forms.stripe.stripeKey);
            this.renderCreditCardElement();
            setTimeout(() => {
                this.view.applyHubspotCss();
            }, 500);
        }

        if (this.step) {
            this.hideForm();
        }

        return Promise.resolve();
    }

    renderCreditCardElement() {
        let style = {};
        if (this.config.settings.stripeElementStyles) {
            style = this.fixJson(this.config.settings.stripeElementStyles);
        }

        const elements = this.stripeAPI.elements();

        if (this.config.settings.stripeSplitElements) {
            const cardNumber = elements.create('cardNumber', {
                style: style
            });
            cardNumber.mount('#stripe_elements_card');
            this.stripeElement = cardNumber;

            const cardExpiry = elements.create('cardExpiry', {
                style: style
            });
            cardExpiry.mount('#stripe_elements_exp');

            const cardCvc = elements.create('cardCvc', {
                style: style
            });
            cardCvc.mount('#stripe_elements_cvc');

        } else {

            this.stripeElement = elements.create('card', {
                hidePostalCode: true,
                style: style
            });

            this.stripeElement.mount('#stripe_elements');
        }
    }

    private fixJson(json: string) {
        try {
            const fixedJson = json.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ').replace(/'/g, '\"');
            return JSON.parse(fixedJson);
        } catch (error: any) {
            this.logger = new Logger(this.config);
            this.logger.log(error.message);
            return {};
        }
    }

    unmount() {
        this.stripeElement.unmount();
    }

    async submit(order: Order) {
        const contact = order.params[0];

        return this.stripeAPI.createPaymentMethod('card', this.stripeElement, this.populateCardData(contact))
            .then((response: any) => {
                this.view.hideError('#stripe_error');
                if (response.error) {
                    this.view.showError(response.error.message, '#stripe_error');
                    this.logger = new Logger(this.config);
                    this.logger.log(response.error.message);
                    return Promise.reject(response.error.message);
                }

                order.paymentMethod = {
                    type: PaymentType.STRIPE,
                    paymentMethodId: response.paymentMethod.id,
                    stripeEmail: contact.email,
                    accountId: this.config.forms.stripe.accountId ? this.config.forms.stripe.accountId : null
                };

                return order;
            });
    }

    private populateCardData(context: any) {
        let name = context.firstname;
        const lastName = context.lastname;
        if (lastName) {
            name += ' ' + lastName;
        }
        const line1 = context.address;
        const city = context.city;
        const postalCode = context.zip;
        const state = context.state;
        const country = context.country;
        return {
            billing_details: {
                name: name ? name : null,
                address: {
                    line1: line1 ? line1 : null,
                    line2: null,
                    city: city ? city : null,
                    postal_code: postalCode ? postalCode : null,
                    state: state ? state : null,
                    // TODO: Ignore country for now, later do mapping to ISO 3166-2 format since Stripe requires it.
                    // country: country ? mapToStripeCountryFormat(country) : null
                    country: null
                }
            }
        };
    }

    postSubmit(submissionContext: any): Promise<any> {
        this.config.widget.triggerAppEvent('onFormSubmitted', submissionContext);
        this.config.widget.redirectOrMessage();
        return Promise.resolve();
    }

    upSellSubmit(upsellResponse: any): Promise<any> {
        this.config.widget.renderUpSell();
        return Promise.resolve();
    }

    /**
     * Function for handling 3D Secure flow depending if it's Payment Intent or Setup Intent.
     */
    handleCardAction(
        paymentIntentClientSecret: string,
        setupIntentClientSecret: string,
        errorContainer: string
    ) {
        if (paymentIntentClientSecret) {
            return this.stripeAPI.handleCardAction(paymentIntentClientSecret)
                .then((response: any) => {
                    if (response.error) {
                        this.view.showError(response.error.message, errorContainer);
                        this.logger = new Logger(this.config);
                        this.logger.log(response.error.message);
                        return {
                            isCardAuthenticated: false,
                            error: response.error.message
                        };
                    }
                    return {
                        isCardAuthenticated: true,
                        paymentIntentId: response.paymentIntent.id
                    };
                });
        } else if (setupIntentClientSecret) {
            return this.stripeAPI.confirmCardSetup(setupIntentClientSecret)
                .then((response: any) => {
                    if (response.error) {
                        this.view.showError(response.error.message, errorContainer);
                        this.logger = new Logger(this.config);
                        this.logger.log(response.error.message);
                        return {
                            isCardAuthenticated: false,
                            error: response.error.message
                        };
                    }
                    return {
                        isCardAuthenticated: true,
                        setupIntentId: response.setupIntent.id
                    };
                });
        } else {
            return {
                isCardAuthenticated: false,
                error: 'Payment Intent Id or Setup Intent Id must be provided.'
            };

        }
    }

    hidePaymentFields() {
        this.view.hideStripeForm();
    }

    showPaymentFields() {
        this.view.showStripeForm();
    }

    showForm() {
        this.view.showForm();
    }

    hideForm() {
        this.view.hideForm();
    }

}
